import { translateBackend } from '@assets/i18n/config';
import { useWorkflowCleanup } from '@ovComponents/3-pattern/cancelWorkFlowDialog/useWorkflowCleanup';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { buildClearedCreatedFields } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { type FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ArchiveObjectsOptions,
  ArchiveObjectsVisual,
} from './archiveObjects.visual';
import { useArchiveObjectsData } from './hooks/useArchiveObjectsData';

export const ArchiveObjects: FC<{
  options: ArchiveObjectsOptions;
  onNext: () => void;
  stepLoading: boolean;
}> = ({ options, onNext, stepLoading }) => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { cleanupWorkflow } = useWorkflowCleanup();
  const [archiving, setArchiving] = useState(false);
  const hasAutoArchived = useRef(false);
  const { t } = useTranslation(['workflowCompletions']);

  const {
    accounts,
    goals,
    subAccounts,
    transfers,
    scheduledTransfers,
    externalTransfers,
    loading: dataLoading,
  } = useArchiveObjectsData(workflowData);

  const custodianDepositTransfers =
    workflowData.createdCustodianDepositTransfers ?? [];

  const presentObjectTypes = [
    { items: accounts, key: options.accountLabel || 'archiveObjects.accounts' },
    { items: goals, key: options.goalLabel || 'archiveObjects.goals' },
    {
      items: subAccounts,
      key: options.subAccountLabel || 'archiveObjects.subAccounts',
    },
    {
      items: transfers,
      key: options.transferLabel || 'archiveObjects.transfers',
    },
    {
      items: scheduledTransfers,
      key:
        options.scheduledTransferLabel || 'archiveObjects.scheduledTransfers',
    },
    {
      items: externalTransfers,
      key: options.externalTransferLabel || 'archiveObjects.externalTransfers',
    },
    {
      items: custodianDepositTransfers,
      key:
        options.custodianDepositTransferLabel ||
        'archiveObjects.custodianDepositTransfers',
    },
  ]
    .filter(({ items }) => items.length > 0)
    .map(({ key }) => translateBackend(key) || t(`workflowCompletions:${key}`))
    .join(', ');

  useEffect(() => {
    if (options.showStepInWorkflow || hasAutoArchived.current) return;
    hasAutoArchived.current = true;

    const runSilentArchive = async () => {
      setArchiving(true);
      try {
        await cleanupWorkflow(workflowData);
        await setWorkflowData((prev) => ({
          ...prev,
          ...buildClearedCreatedFields(),
        }));
        onNext();
      } catch {
        setArchiving(false);
      }
    };

    runSilentArchive();
  }, [
    options.showStepInWorkflow,
    cleanupWorkflow,
    workflowData,
    onNext,
    setWorkflowData,
  ]);

  const handleSubmit = async () => {
    setArchiving(true);
    try {
      await cleanupWorkflow(workflowData);
      await setWorkflowData((prev) => ({
        ...prev,
        ...buildClearedCreatedFields(),
      }));
      onNext();
    } catch {
      setArchiving(false);
    }
  };

  return (
    <ArchiveObjectsVisual
      options={options}
      loading={stepLoading || dataLoading}
      archiving={archiving}
      onSubmit={handleSubmit}
      presentObjectTypes={presentObjectTypes}
      accounts={accounts}
      goals={goals}
      subAccounts={subAccounts}
      transfers={transfers}
      scheduledTransfers={scheduledTransfers}
      externalTransfers={externalTransfers}
      custodianDepositTransfers={custodianDepositTransfers}
    />
  );
};
